import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import useAuth from 'hooks/useAuth';
import { useBillingClient } from 'client/client-hook';
import { useDispatch } from 'store';
import { DispatchSnackbarService } from 'services';
import { stripeForKey, userStripeKey, isRetryableStripeError, StripeJsLoadError, STRIPE_UNAVAILABLE_MESSAGE } from 'lib/stripe/userStripe';

const service = new DispatchSnackbarService();

// One snackbar per user per session: this hook is mounted by a dozen billing
// components at once, and they must not stack a dozen identical toasts.
const notified = new Set<string>();

/**
 * Forgets which users have already been warned.
 *
 * Called on sign-out (below) so the next session warns again, and by
 * useUserStripe.test.tsx to isolate cases from one another.
 */
export function resetUserStripeNotifications(): void {
  notified.clear();
}

// Retries are shared, not per-instance. SubscriptionsPage alone mounts several
// consumers at once, and a "Try again" that revived only the alert the user
// happened to click — leaving its identical neighbours broken — is the kind of
// surprise that reads as a bug. One counter plus a listener set is cheaper than
// the confusion: a bump re-runs every mounted instance, and because
// `userStripeKey` memoises per user they share a single backend call.
let retryGeneration = 0;
const generationListeners = new Set<(generation: number) => void>();

function bumpRetryGeneration(): void {
  retryGeneration += 1;
  // Copy: a listener that unsubscribes during the walk must not skip a sibling.
  Array.from(generationListeners).forEach((listener) => listener(retryGeneration));
}

export type UserStripeState = {
  /**
   * Stripe.js promise for the signed-in user's Stripe account.
   * `null` while the user's account is still being resolved, and `null` again
   * once `error` is set — Stripe.js is deliberately never loaded on the error
   * path, and a null promise keeps `<Elements>` from mounting a card form that
   * could never confirm its intent.
   */
  stripe: Promise<Stripe | null> | null;
  /**
   * Set when this user has no usable Stripe.js: the reported account is
   * unmappable, billing was unreachable, or Stripe.js itself never loaded.
   *
   * A component that renders payment UI MUST check this and show
   * `<StripeAccountUnavailable error={error} />` instead: `stripe` stays null
   * forever here, so any onReady-driven "loaded" flag never flips and an
   * unguarded surface spins for good.
   */
  error: Error | null;
  /**
   * True when `error` is one a retry could still resolve — billing unreachable,
   * or Stripe.js unfetched — false for an unmappable account, which cannot come
   * right without a redeploy.
   */
  retryable: boolean;
  /**
   * Re-attempts resolution. A no-op unless `retryable`.
   *
   * Retries every mounted consumer, not just the one that was clicked — see
   * the note above `retryGeneration`.
   */
  retry: () => void;
};

/**
 * Stripe.js promise plus the resolution error, for components that render an
 * explicit failure instead of an endless spinner.
 */
export function useUserStripeState(): UserStripeState {
  const { user } = useAuth() as { user?: { tcId?: string } };
  const getBillingClient = useBillingClient();
  const dispatch = useDispatch();
  const userId = user?.tcId;
  const [key, setKey] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  // Start level with the shared counter: a consumer mounting after someone
  // else's retry is already looking at the current generation.
  const [attempt, setAttempt] = useState(retryGeneration);

  // Only read inside the rejection callback, never an input to resolution.
  // Keeping it out of the effect deps means an unstable dispatch identity
  // cannot re-run the lookup (and, with the error reset below, spin forever).
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  // Same treatment, same reason. `useBillingClient` re-memoises on
  // `refreshToken`, which Auth0Context re-creates on every one of its renders,
  // so this identity changes constantly. As an effect dep it would re-issue the
  // BillingOverview lookup on every provider render for as long as billing is
  // unreachable — a fresh error and a consumer re-render each time, with no
  // backoff. What client to use is an input to the CALL, not to the question of
  // whether to ask, so the effect reads the latest one when it runs instead.
  const getBillingClientRef = useRef(getBillingClient);
  getBillingClientRef.current = getBillingClient;

  // Shared retries: adopt any bump that happened while this instance was
  // mounted, and re-sync on subscribe to close the gap between first render and
  // this effect. Equal values are a React bail-out, so this cannot loop.
  useEffect(() => {
    const listener = (generation: number) => {
      // Clear here rather than only in `retry` so a retry started on ANOTHER
      // surface also drops this one's stale error, not just its own.
      setError(null);
      setAttempt(generation);
    };
    generationListeners.add(listener);
    setAttempt(retryGeneration);
    return () => {
      generationListeners.delete(listener);
    };
  }, []);

  // A different user is a fresh question, so drop the previous one's outcome.
  // Keyed on userId ALONE: resetting state from the resolving effect below
  // would make that effect re-trigger itself on every render.
  useEffect(() => {
    setKey(null);
    setError(null);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      // Signed out. Drop the "already warned" marks so the next session — the
      // same user signing back in included — sees the warning again.
      resetUserStripeNotifications();
      return undefined;
    }
    let cancelled = false;
    userStripeKey(userId, getBillingClientRef.current).then(
      (k) => {
        if (!cancelled) {
          setKey(k);
          // A retry that worked has to clear the error it was retrying,
          // including on instances whose `retry` was never the one clicked.
          setError(null);
        }
      },
      (err: Error) => {
        // Fail loudly: log the detail, tell the user once, load nothing.
        console.error('[stripe] cannot resolve a publishable key for user', userId, err);
        if (!notified.has(userId)) {
          notified.add(userId);
          service.dispatchSnackbar(dispatchRef.current, err?.message || STRIPE_UNAVAILABLE_MESSAGE, 'alert', 'error', 8000);
        }
        if (!cancelled) {
          setKey(null);
          setError(err);
        }
      }
    );
    return () => {
      cancelled = true;
    };
    // `getBillingClient` is deliberately absent: see `getBillingClientRef`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, attempt]);

  const retryable = isRetryableStripeError(error);

  const retry = useCallback(() => {
    // Only the transport failure and the failed script load are worth
    // re-asking: an unmappable account is a build-time fact, and userStripeKey
    // has cached the refusal anyway.
    if (!retryable) return;
    // Neither retryable failure is cached — userStripeKey never stores the
    // transport rejection and stripeForKey evicts the key it could not load —
    // so bumping the effect really does re-ask rather than replay.
    notified.delete(userId || '');
    setError(null);
    // Shared, so the sibling alerts on this page recover with this one. The
    // listener set includes our own, which is what advances `attempt`.
    bumpRetryGeneration();
  }, [retryable, userId]);

  const stripe = useMemo(() => {
    // No key, or a key we refuse to guess at → no Stripe.js, and no promise for
    // <Elements> to mount on.
    if (error || !key) return null;
    return stripeForKey(key);
  }, [key, error]);

  // The key can be right and Stripe.js still never arrive — an ad blocker, an
  // offline tab, a CDN blip. `stripeForKey` resolves null for that rather than
  // handing back loadStripe's rejection, because a rejected promise is still
  // TRUTHY: it passes every `if (!stripe)` guard, <Elements> mounts on it,
  // react-stripe-js never catches, and the surface spins forever. A null
  // instance is as fatal as an unresolvable key, so raise it as the same
  // visible error — retryable, since stripeForKey evicted the cache entry and
  // the next call really re-fetches the script.
  //
  // Setting `error` makes the memo above return null, so this effect re-runs
  // with nothing to await and stops. It cannot feed itself.
  useEffect(() => {
    if (!stripe) return undefined;
    let cancelled = false;
    stripe.then((instance) => {
      if (!instance && !cancelled) {
        console.error('[stripe] Stripe.js did not load for user', userId);
        setError(new StripeJsLoadError());
      }
    });
    return () => {
      cancelled = true;
    };
  }, [stripe, userId]);

  return { stripe, error, retryable, retry };
}
